import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Tag, Clock, Heart, Car } from 'lucide-react';
import type { FavoriteItem } from '../../types/favorites';
import { WalkIcon, BikeIcon, GiftIcon } from '../icons';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { Toast } from '../shared/Toast';
import { useTranslatedContent } from '../../hooks/useTranslatedContent';
import { isEmptyField } from '../../utils/timeUtils';
import { resolveColor } from '../../utils/colorUtils';
import { formatPhoneNumber } from '../../utils/formatters';
import { formatTravelTime, formatTimeRange } from '../../utils/formatDuration';
import { useImageDisplay } from '../../hooks/useImageDisplay';

interface FavoritesListProps {
  items: FavoriteItem[];
  onItemClick: (item: FavoriteItem) => void;
  onRemoveFavorite: (itemId: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  categoryColors: Map<string, string>;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
}

export const FavoritesList: React.FC<FavoritesListProps> = ({
  items,
  onItemClick,
  onRemoveFavorite,
  config,
  translations,
  categoryColors,
  onCategorySelect,
  onTypeSelect
}) => {
  const { i18n } = useTranslation();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Get translations from trad_txt
  const { translations: pageTranslations } = useTranslatedContent({
    terms: ['no_favorites', 'Lundi', 'in_hotel']
  });

  const getTranslatedCategory = (category: string): string => {
    const translation = translations[category];
    if (!translation) return category;
    return translation[i18n.language] || translation.fr || category;
  };

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const backgroundColor = resolveColor(config['sectionBack-color'], config, '#f9fafb');

  // Create a separate component for each item
  const ListItemCard = React.memo(({ item }: { item: FavoriteItem }) => {
    const [isFavorite, setIsFavorite] = React.useState(true);

    const handleFavoriteToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemoveFavorite(item.id);
      setIsFavorite(false);
      
      // Show toast
      const message = translations['removed_from_favorites']?.[i18n.language] || 
                     translations['removed_from_favorites']?.fr || 
                     'Retiré des favoris';
      setToastMessage(message);
      setShowToast(true);

      // Track event
      analyticsService.trackItemAction(
        'favorites',
        item.id,
        'remove_favorite',
        'favorite',
        {
          source: 'favorites_section',
          section_name: 'Favorites',
          section_type: 'favorites'
        }
      );
    };

    // Get image to use (item image or replacement image)
    const { displayImage, shouldDisplay } = useImageDisplay(
      item.data.image || item.metadata.sourceSection.replace_img?.[0]?.url,
      'listCard'
    );

    // Render content based on source type
    const renderItemContent = () => {
      switch (item.sourceType) {
        case 'event':
        case 'event-api':
          return (
            <>
              <div className="flex items-center text-sm text-gray-600 mb-1">
                <MapPin size={16} className="mr-1 shrink-0" />
                <div className="flex items-center w-full">
                  <span className="shrink-0 whitespace-nowrap">{item.data.distance}km</span>
                  <span className="mx-1 shrink-0">•</span>
                  <span className="truncate max-w-[calc(100%-60px)] sm:max-w-[150px] md:max-w-[180px] lg:max-w-[200px] xl:max-w-[250px]">
                    {item.data.place}
                  </span>
                </div>
              </div>

              {item.data.artiste && (
                <p className="text-sm text-gray-600 mb-1">{item.data.artiste}</p>
              )}

              <div className="flex flex-wrap items-center gap-2 text-sm mb-2">
                {item.data.hour && (
                  <div className="flex items-center text-gray-600">
                    <Clock size={16} className="mr-1" />
                    <span>
                      {new Intl.DateTimeFormat(i18n.language, {
                        day: 'numeric',
                        month: 'short'
                      }).format(new Date(item.data.date))}
                      {' - '}
                      {new Intl.DateTimeFormat(i18n.language, {
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: i18n.language === 'en'
                      }).format(new Date(`2000-01-01T${item.data.hour}`))}
                    </span>
                  </div>
                )}
                
                {item.data.price && (
                  <div 
                    className="flex items-center"
                    style={{ color: config['major-color'] }}
                  >
                    <Tag size={16} className="mr-1" />
                    <span>{item.data.price}</span>
                  </div>
                )}
              </div>
            </>
          );
        case 'standard':
          return (
            <>
              {item.data.fulladress && (
                <p className="text-sm text-gray-600 mb-2 line-clamp-1">{item.data.fulladress}</p>
              )}
              
              <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-3">
                {/* In hotel or distance */}
                {item.data.inHotel ? (
                  <div className="flex items-center">
                    <MapPin size={16} className="mr-1" />
                    <span>{pageTranslations?.in_hotel?.[i18n.language] || pageTranslations?.in_hotel?.fr || 'Dans l\'hôtel'}</span>
                  </div>
                ) : (
                  <>
                    {!isEmptyField(item.data.distance) && (
                      <div className="flex items-center">
                        <MapPin size={16} className="mr-1" />
                        <span>{item.data.distance}km</span>
                      </div>
                    )}
                    
                    {/* Travel times */}
                    {!isEmptyField(item.data.timewalk) && (
                      <div className="flex items-center">
                        <WalkIcon size={16} className="mr-1" />
                        <span>{formatTravelTime(item.data.timewalk, i18n.language)}</span>
                      </div>
                    )}
                  </>
                )}
                
                {/* Monday opening */}
                {item.sourceType === 'standard' && item.data.ouverture_lundi === 'oui' && (
                  <div className="flex items-center">
                    <Clock size={16} className="mr-1" />
                    <span>{pageTranslations?.Lundi?.[i18n.language] || pageTranslations?.Lundi?.fr || 'Lundi'}</span>
                  </div>
                )}
                
                {/* Price */}
                {item.data.price && (
                  <div 
                    className="flex items-center"
                    style={{ color: config['major-color'] }}
                  >
                    <Tag size={16} className="mr-1" />
                    <span>{item.data.price}</span>
                  </div>
                )}
              </div>
            </>
          );
        case 'liste':
          return (
            <>
              {item.data.fulladress && (
                <p className="text-sm text-gray-600 mb-2 line-clamp-1">{item.data.fulladress}</p>
              )}
            </>
          );
        default:
          return null;
      }
    };

    return (
      <div
        className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 relative"
        style={{ borderRadius: config.border_radius }}
      >
        <div 
          className="cursor-pointer relative"
          onClick={() => {
            analyticsService.trackItemAction(
              'favorites',
              item.id,
              'item_click',
              item.sourceType,
              {
                item_name: item.data.name,
                category: item.data.category,
                type: item.data.type,
                section_name: 'Favorites',
                section_type: 'favorites'
              }
            );
            onItemClick(item);
          }}
        >
          {/* Favorite button */}
          <button
            onClick={handleFavoriteToggle}
            className="absolute top-0 left-0 w-12 h-12 flex items-center justify-center transition-opacity hover:opacity-80 z-10"
            style={{ 
              backgroundColor: config['major-color'],
              borderTopLeftRadius: config.border_radius,
              borderBottomRightRadius: config.border_radius
            }}
            aria-label="Retirer des favoris"
          >
            <Heart
              size={24}
              className="text-white"
              fill="currentColor"
            />
          </button>

          {/* Gift icon for items with offers */}
          {item.data.offer && (
            <div 
              className="absolute top-0 right-0 w-10 h-10 flex items-center justify-center z-10"
              style={{ 
                backgroundColor: config['major-color'],
                borderTopRightRadius: config.border_radius,
                borderBottomLeftRadius: config.border_radius
              }}
            >
              <GiftIcon size={20} className="text-white" />
            </div>
          )}

          {/* Image */}
          {shouldDisplay && displayImage && (
            <div className="relative pb-[66.666%]">
              <img
                src={displayImage}
                alt={item.data.name}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="p-3">
            <h3 className="text-lg font-medium mb-1">{item.data.name}</h3>
            
            {/* Content based on source type */}
            {renderItemContent()}

            {/* Categories and types */}
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  analyticsService.trackEvent({
                    event_type: 'filter',
                    event_name: 'category_filter',
                    event_data: {
                      section_id: 'favorites',
                      section_name: 'Favorites',
                      section_type: 'favorites',
                      filter_value: item.data.category
                    }
                  });
                  onCategorySelect(item.data.category);
                }}
                className="inline-block px-1.5 py-0.5 text-xs rounded hover:opacity-80 transition-opacity text-white"
                style={{ backgroundColor: categoryColors.get(item.data.category) || config['medium-color'] }}
              >
                {getTranslatedCategory(item.data.category)}
              </button>
              
              {item.data.type && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    analyticsService.trackEvent({
                      event_type: 'filter',
                      event_name: 'type_filter',
                      event_data: {
                        section_id: 'favorites',
                        section_name: 'Favorites',
                        section_type: 'favorites',
                        filter_value: item.data.type
                      }
                    });
                    onTypeSelect(item.data.type);
                  }}
                  className="inline-block px-1.5 py-0.5 text-xs rounded hover:opacity-80 transition-opacity text-white opacity-70"
                  style={{ backgroundColor: config['medium-color'] }}
                >
                  {getTranslatedCategory(item.data.type)}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  });

  if (items.length === 0) {
    return (
      <div 
        className="flex items-center justify-center min-h-screen"
        style={{ backgroundColor }}
      >
        <div className="max-w-lg px-8">
          <p 
            className="text-gray-500 text-center"
            dangerouslySetInnerHTML={{ 
              __html: pageTranslations?.no_favorites?.[i18n.language] || 
                     pageTranslations?.no_favorites?.fr || 
                     'Aucun favori'
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div 
        className="w-full min-h-screen pb-20"
        style={{ backgroundColor }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => (
              <ListItemCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </div>

      {showToast && (
        <Toast
          message={toastMessage}
          type="success"
          onClose={() => setShowToast(false)}
          duration={2000}
          icon={<Heart size={18} className="text-white" fill="currentColor" />}
          config={config}
        />
      )}
    </>
  );
};